/**
 * React Query Configuration
 *
 * Configures Tanstack Query client with aggressive caching strategies
 * for optimal performance and minimal API calls.
 *
 * @module lib/react-query
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Query client with aggressive caching configuration
 *
 * Configuration strategy:
 * - Long stale times to minimize refetches
 * - Extended garbage collection for better caching
 * - Disabled automatic refetching on focus/mount
 * - Background revalidation on reconnect
 * - Optimistic placeholderData for instant UX
 *
 * @example
 * ```tsx
 * import { QueryClientProvider } from '@tanstack/react-query';
 * import { queryClient } from '@/lib/react-query';
 *
 * <QueryClientProvider client={queryClient}>
 *   <App />
 * </QueryClientProvider>
 * ```
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Cache data for 15 minutes before considering it stale
       * This reduces unnecessary API calls for relatively static data
       */
      staleTime: 15 * 60 * 1000,

      /**
       * Keep unused data in cache for 30 minutes
       * Allows quick access to previously viewed data
       */
      gcTime: 30 * 60 * 1000,

      /**
       * Retry failed requests once
       * Helps with transient network issues without excessive retries
       */
      retry: 1,

      /**
       * Disable refetch on window focus
       * Prevents unnecessary API calls when switching browser tabs
       */
      refetchOnWindowFocus: false,

      /**
       * Disable refetch on component mount
       * Uses cached data if available and not stale
       */
      refetchOnMount: false,

      /**
       * Enable refetch on network reconnect
       * Ensures data is fresh after connectivity is restored
       */
      refetchOnReconnect: true,

      /**
       * Use previous data while revalidating
       * Provides instant UI feedback with cached data
       */
      placeholderData: (previousData: unknown) => previousData,
    },
    mutations: {
      /**
       * Retry failed mutations once
       * Helps with transient issues but avoids duplicates
       */
      retry: 1,
    },
  },
});

/**
 * Custom stale time configurations for different data types
 *
 * Use these with individual queries for fine-tuned caching
 */
export const staleTimes = {
  /** 1 hour - for rarely changing data (provinces, regions, etc.) */
  geography: 60 * 60 * 1000,

  /** 5 minutes - for frequently updated aggregated data (dashboard stats) */
  stats: 5 * 60 * 1000,

  /** 15 minutes - for moderately changing data (team members) */
  teams: 15 * 60 * 1000,

  /** 30 minutes - for lookup data (wings, designations) */
  lookups: 30 * 60 * 1000,

  /** 1 minute - for real-time data (if needed) */
  realtime: 1 * 60 * 1000,
};

/**
 * Query key factory for consistent cache keys
 *
 * Provides standardized query keys to avoid typos and ensure
 * proper cache invalidation.
 *
 * @example
 * ```tsx
 * useQuery({
 *   queryKey: queryKeys.provinces.all,
 *   queryFn: geographyApi.getProvinces,
 * });
 *
 * useQuery({
 *   queryKey: queryKeys.regions.byProvince(provinceId),
 *   queryFn: () => geographyApi.getRegions(provinceId),
 * });
 * ```
 */
export const queryKeys = {
  // Geography
  provinces: {
    all: ['provinces'] as const,
    detail: (id: number) => ['provinces', id] as const,
  },
  regions: {
    all: ['regions'] as const,
    byProvince: (provinceId: number) => ['regions', { provinceId }] as const,
    detail: (id: number) => ['regions', id] as const,
  },
  districts: {
    all: ['districts'] as const,
    byRegion: (regionId: number) => ['districts', { regionId }] as const,
    detail: (id: number) => ['districts', id] as const,
  },
  towns: {
    all: ['towns'] as const,
    byDistrict: (districtId: number) => ['towns', { districtId }] as const,
    detail: (id: number) => ['towns', id] as const,
  },
  ucs: {
    all: ['ucs'] as const,
    byTown: (townId: number) => ['ucs', { townId }] as const,
    detail: (id: number) => ['ucs', id] as const,
  },

  // Teams
  teams: {
    province: ['teams', 'province'] as const,
    region: ['teams', 'region'] as const,
    district: ['teams', 'district'] as const,
    town: ['teams', 'town'] as const,
    uc: ['teams', 'uc'] as const,
  },

  // Stats
  stats: {
    dashboard: ['stats', 'dashboard'] as const,
    geographic: ['stats', 'geographic'] as const,
  },

  // Lookups
  lookups: {
    wings: ['lookups', 'wings'] as const,
    designations: ['lookups', 'designations'] as const,
  },
};